// Types
import { Driver } from "@/types/supabase";

// Libs
import { useState, useEffect } from "react";

// Hooks
import { useDriversApi } from "@/hooks/useDriversApi";
import { useBranches } from "@/hooks/useBranches";

// Components
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/atoms/dialog";
import { Input } from "@/components/atoms/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/atoms/select";
import { Button } from "@/components/atoms/button";

// Icons
import { CheckCircle2 } from "lucide-react";

import { toastHandler } from "@/lib/ToastHandler";
import { getDriverIdentityConflict } from "@/lib/driverIdentityConflict";

interface DriverModalProps {
  trigger: React.ReactNode;
  onClose?: () => void;
  driverToEdit?: Driver | null;
  refreshDrivers?: () => Promise<void>;
}

// Helper to map vehicle_type for display and storage
const VEHICLE_TYPE_OPTIONS = [
  { value: "bicycle", label: "Bicicleta" },
  { value: "moto", label: "Motocicleta" },
  { value: "car", label: "Automóvil" },
];

// Map legacy "motorcycle" to "moto" for formData and initial values
function normalizeVehicleType(type: string | undefined | null): string {
  if (type === "motorcycle") return "moto";
  if (type === "moto") return "moto";
  if (type === "bicycle") return "bicycle";
  if (type === "car") return "car";
  return "moto"; // default
}

// When saving, always store "moto" as "moto"
function toStorageVehicleType(type: string): string {
  if (type === "moto" || type === "motorcycle") return "moto";
  return type;
}

export default function DriverModal({
  trigger,
  onClose,
  driverToEdit,
  refreshDrivers,
}: DriverModalProps) {
  const { createDriver, updateDriver } = useDriversApi();
  const { branches, loading: loadingBranches, fetchBranches } = useBranches();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const isEditMode = !!driverToEdit;
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [initialFormData, setInitialFormData] = useState<
    typeof formData | null
  >(null);
  const [formData, setFormData] = useState({
    name: driverToEdit?.name || "",
    email: driverToEdit?.email || "",
    address: driverToEdit?.address || "",
    document: driverToEdit?.document || "",
    phone: driverToEdit?.phone || "",
    branch_id: driverToEdit?.branch_id || null,
    worker_type: driverToEdit?.worker_type || "full-time",
    vehicle_type: normalizeVehicleType(driverToEdit?.vehicle_type) || "moto",
  });
  useEffect(() => {
    fetchBranches();
  }, []);
  // Reset form data when driverToEdit changes to prevent stale data
  useEffect(() => {
    const newFormData = {
      name: driverToEdit?.name || "",
      email: driverToEdit?.email || "",
      address: driverToEdit?.address || "",
      document: driverToEdit?.document || "",
      phone: driverToEdit?.phone || "",
      branch_id: driverToEdit?.branch_id || null,
      worker_type: driverToEdit?.worker_type || "full-time",
      vehicle_type: normalizeVehicleType(driverToEdit?.vehicle_type) || "moto",
    };
    setFormData(newFormData);

    // Set initial form data for edit mode to track changes
    if (driverToEdit) {
      setInitialFormData(newFormData);
    }
  }, [driverToEdit]);

  // Branches are auto-fetched and cached by useBranches

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      address: "",
      document: "",
      phone: "",
      branch_id: null,
      worker_type: "full-time",
      vehicle_type: "moto",
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;

    // Real-time validation for name field - only allow letters and spaces
    if (id === "name") {
      const filteredValue = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s'-]/g, "");
      setFormData({
        ...formData,
        [id]: filteredValue,
      });
    }
    // Real-time validation for phone field - only allow numbers
    else if (id === "phone") {
      const filteredValue = value.replace(/[^0-9]/g, "");
      setFormData({
        ...formData,
        [id]: filteredValue,
      });
    }
    // Default handling for other fields
    else {
      setFormData({
        ...formData,
        [id]: value,
      });
    }
  };

  const handleSelectChange = (id: string, value: string) => {
    setFormData({
      ...formData,
      [id]: id === "branch_id" ? (value ? parseInt(value) : null) : value,
    });
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "El nombre es requerido";
    } else if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s'-]+$/.test(formData.name.trim())) {
      newErrors.name =
        "El nombre solo puede contener letras, guiones y apóstrofes";
    } else if (formData.name.trim().length < 3) {
      newErrors.name = "El nombre debe tener al menos 3 caracteres";
    } else if (formData.name.trim().length > 50) {
      newErrors.name = "El nombre no puede tener más de 50 caracteres";
    }

    if (!formData.email.trim()) {
      newErrors.email = "El email es requerido";
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.email)) {
      newErrors.email = "Por favor ingresa un email válido";
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = "El teléfono es requerido";
    } else if (!/^[0-9]+$/.test(formData.phone.trim())) {
      newErrors.phone = "El teléfono solo puede contener números";
    } else if (formData.phone.trim().length < 7) {
      newErrors.phone = "El teléfono debe tener al menos 7 caracteres";
    } else if (formData.phone.trim().length > 20) {
      newErrors.phone = "El teléfono no puede tener más de 20 caracteres";
    }

    if (!formData.branch_id) {
      newErrors.branch_id = "Debes seleccionar una sucursal";
    }

    // Document validation
    if (!formData.document.trim()) {
      newErrors.document = "El documento es requerido";
    } else if (formData.document.trim().length < 4) {
      newErrors.document = "El documento debe tener al menos 4 caracteres";
    } else if (formData.document.trim().length > 20) {
      newErrors.document = "El documento no puede tener más de 20 caracteres";
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { isValid, errors: validationErrors } = validate();

    if (!isValid) {
      const errorFields = Object.keys(validationErrors).map((field) => {
        switch (field) {
          case "name":
            return "Nombre";
          case "email":
            return "Email";
          case "phone":
            return "Teléfono";
          case "branch_id":
            return "Sucursal";
          case "document":
            return "Documento";
          default:
            return field;
        }
      });

      toastHandler({
        description: `Por favor corrige los siguientes campos: ${errorFields.join(", ")}`,
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    const normalizedEmail = formData.email.trim().toLowerCase();
    const normalizedDocument = formData.document.trim();

    try {
      if (driverToEdit) {
        // Edit mode - only send changed fields
        const updateData: any = {};
        let hasChanges = false;

        // Compare each field with initial values
        if (initialFormData) {
          if (formData.name !== initialFormData.name) {
            updateData.name = formData.name;
            hasChanges = true;
          }
          if (normalizedEmail !== initialFormData.email.trim().toLowerCase()) {
            updateData.email = normalizedEmail;
            hasChanges = true;
          }
          if (formData.address !== initialFormData.address) {
            updateData.address = formData.address;
            hasChanges = true;
          }
          if (normalizedDocument !== initialFormData.document.trim()) {
            updateData.document = normalizedDocument;
            hasChanges = true;
          }
          if (formData.phone !== initialFormData.phone) {
            updateData.phone = formData.phone;
            hasChanges = true;
          }
          if (formData.vehicle_type !== initialFormData.vehicle_type) {
            updateData.vehicle_type = toStorageVehicleType(
              formData.vehicle_type,
            );
            hasChanges = true;
          }
          if (formData.worker_type !== initialFormData.worker_type) {
            updateData.worker_type = formData.worker_type;
            hasChanges = true;
          }
          if (formData.branch_id !== initialFormData.branch_id) {
            updateData.branch_id = formData.branch_id;
            hasChanges = true;
          }
        }

        if (hasChanges) {
          await updateDriver(driverToEdit.id, updateData);
          toastHandler({
            description: "Se ha actualizado la información del conductor.",
            variant: "success",
          });
        } else {
          toastHandler({
            description: "No se detectaron cambios para guardar.",
            variant: "default",
          });
        }

        if (refreshDrivers) await refreshDrivers();
        setIsOpen(false);
      } else {
        // Create mode - send all data
        const driverData = {
          name: formData.name,
          email: normalizedEmail,
          address: formData.address,
          document: normalizedDocument,
          phone: formData.phone,
          vehicle_type: toStorageVehicleType(formData.vehicle_type),
          worker_type: formData.worker_type,
          branch_id: formData.branch_id,
          available: false,
          active: false,
        };

        await createDriver(driverData);
        if (refreshDrivers) await refreshDrivers();
        setShowSuccess(true);
        resetForm();
        // No cerrar el modal aquí, mostrar el mensaje de éxito
      }
    } catch (error) {
      console.error("Error creating driver:", error);
      const identityConflict = getDriverIdentityConflict(error);
      if (identityConflict) {
        setErrors((currentErrors) => ({
          ...currentErrors,
          [identityConflict.field]: identityConflict.message,
        }));
        toastHandler({
          description: identityConflict.message,
          variant: "destructive",
        });
      } else {
        toastHandler({
          description: isEditMode
            ? "No se pudo actualizar el conductor. Inténtalo de nuevo."
            : "No se pudo crear el conductor. Inténtalo de nuevo.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
      // No resetear form ni cerrar modal aquí, se maneja en cada caso específico
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          setErrors({});
          setShowSuccess(false);
          if (onClose) {
            onClose();
          }
        }
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="w-screen h-screen max-w-none mx-0 rounded-none sm:max-w-lg sm:w-full sm:mx-4 sm:max-h-[95vh] sm:h-auto sm:rounded-xl p-0 shadow-2xl border-0 bg-white overflow-y-auto"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {!showSuccess ? (
          <div className="flex flex-col h-full sm:max-h-[95vh]">
            {/* Header - Fixed */}
            <DialogHeader className="px-4 py-4 sm:px-6 sm:py-5 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50 flex-shrink-0 safe-area-top">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-white shadow-sm border border-gray-100 flex items-center justify-center flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    className="sm:w-6 sm:h-6 text-blue-600"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1 text-left">
                  <DialogTitle className="text-lg sm:text-xl font-semibold text-gray-900 break-words leading-tight">
                    {isEditMode ? "Editar conductor" : "Nuevo conductor"}
                  </DialogTitle>
                  <DialogDescription className="text-gray-600 text-sm break-words leading-tight mt-1">
                    {isEditMode
                      ? "Modifica la información del conductor"
                      : "Añade un nuevo conductor al sistema"}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            {/* Form Content - Scrollable with proper overflow handling */}
            <form
              onSubmit={handleSubmit}
              className="flex flex-col flex-1 min-h-0 "
            >
              <div className="flex-1  overscroll-contain p-4 sm:p-6 space-y-4 sm:space-y-5">
                {/* Personal Information Section */}
                <div className="space-y-3 sm:space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide border-b border-gray-100 pb-2">
                    Información Personal
                  </h3>

                  <div className="space-y-2">
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Nombre Completo *
                    </label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Ingresa el nombre completo"
                      className={`h-10 sm:h-11 transition-all duration-200 ${
                        errors.name
                          ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                          : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                      }`}
                    />
                    {errors.name && (
                      <p className="text-sm text-red-600 flex items-center gap-1 mt-1">
                        <svg
                          className="w-4 h-4 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="break-words">{errors.name}</span>
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Email *
                    </label>
                    <Input
                      id="email"
                      type="email"
                      aria-invalid={Boolean(errors.email)}
                      aria-describedby={
                        errors.email ? "driver-email-error" : undefined
                      }
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="conductor@ejemplo.com"
                      className={`h-10 sm:h-11 transition-all duration-200 ${
                        errors.email
                          ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                          : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                      }`}
                    />
                    {errors.email && (
                      <p
                        id="driver-email-error"
                        className="text-sm text-red-600 flex items-center gap-1 mt-1"
                      >
                        <svg
                          className="w-4 h-4 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="break-words">{errors.email}</span>
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="space-y-2">
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Teléfono
                      </label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 234 567 8900"
                        className={`h-10 sm:h-11 transition-all duration-200 ${
                          errors.phone
                            ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                            : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                        }`}
                      />
                      {errors.phone && (
                        <p className="text-sm text-red-600 flex items-center gap-1 mt-1">
                          <svg
                            className="w-4 h-4 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="break-words">{errors.phone}</span>
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label
                        htmlFor="document"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Documento
                      </label>
                      <Input
                        id="document"
                        value={formData.document}
                        onChange={handleChange}
                        placeholder="Número de identificación"
                        className={`h-10 sm:h-11 transition-all duration-200 ${
                          errors.document
                            ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                            : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                        }`}
                      />
                      {errors.document && (
                        <p className="text-sm text-red-600 flex items-center gap-1 mt-1">
                          <svg
                            className="w-4 h-4 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="break-words">{errors.document}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="address"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Dirección
                    </label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Dirección completa"
                      className={`h-10 sm:h-11 transition-all duration-200 ${
                        errors.address
                          ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                          : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                      }`}
                    />
                    {errors.address && (
                      <p className="text-sm text-red-600 flex items-center gap-1 mt-1">
                        <svg
                          className="w-4 h-4 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="break-words">{errors.address}</span>
                      </p>
                    )}
                  </div>
                </div>

                {/* Work Information Section */}
                <div className="space-y-3 sm:space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide border-b border-gray-100 pb-2">
                    Información Laboral
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="space-y-2">
                      <label
                        htmlFor="branch_id"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Sucursal *
                      </label>
                      <Select
                        value={
                          formData.branch_id
                            ? String(formData.branch_id)
                            : "unselected"
                        }
                        onValueChange={(v: string) =>
                          handleSelectChange(
                            "branch_id",
                            v === "unselected" ? "" : v,
                          )
                        }
                        disabled={loadingBranches}
                      >
                        <SelectTrigger
                          id="branch_id"
                          className={`h-10 sm:h-11 transition-all duration-200 ${
                            errors.branch_id
                              ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                              : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                          }`}
                        >
                          {loadingBranches ? (
                            <div className="flex items-center gap-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                              <span className="text-gray-500">
                                Cargando sucursales...
                              </span>
                            </div>
                          ) : (
                            <SelectValue placeholder="Seleccionar sucursal" />
                          )}
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unselected">
                            Seleccionar sucursal
                          </SelectItem>
                          {branches.map((branch) => (
                            <SelectItem
                              key={branch.id}
                              value={String(branch.id)}
                            >
                              {branch.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.branch_id && (
                        <p className="text-sm text-red-600 flex items-center gap-1 mt-1">
                          <svg
                            className="w-4 h-4 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="break-words">
                            {errors.branch_id}
                          </span>
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label
                        htmlFor="worker_type"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Tipo de Contrato
                      </label>
                      <Select
                        value={formData.worker_type}
                        onValueChange={(v: string) =>
                          handleSelectChange("worker_type", v)
                        }
                      >
                        <SelectTrigger
                          id="worker_type"
                          className="h-10 sm:h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-200 transition-all duration-200"
                        >
                          <SelectValue placeholder="Seleccionar tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full-time">
                            Tiempo Completo
                          </SelectItem>
                          <SelectItem value="part-time">
                            Medio Tiempo
                          </SelectItem>
                          <SelectItem value="freelance">Freelance</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 sm:space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide border-b border-gray-100 pb-2">
                    Información Vehículo
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="space-y-2">
                      <label
                        htmlFor="vehicle_type"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Tipo de Vehículo *
                      </label>
                      <Select
                        value={formData.vehicle_type}
                        onValueChange={(v) =>
                          handleSelectChange("vehicle_type", v)
                        }
                      >
                        <SelectTrigger
                          id="vehicle_type"
                          className="h-10 sm:h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-200 transition-all duration-200"
                        >
                          <SelectValue placeholder="Seleccionar vehículo" />
                        </SelectTrigger>
                        <SelectContent>
                          {VEHICLE_TYPE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer - Fixed with proper mobile padding */}
              <div className="flex-shrink-0 bg-gray-50 border-t border-gray-100 p-4 pb-6 sm:px-6 sm:py-4">
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      if (onClose) onClose();
                      setIsOpen(false);
                    }}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2 h-10 sm:h-11 border border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 order-2 sm:order-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2 h-10 sm:h-11 bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed order-1 sm:order-2"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center gap-2">
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span>
                          {isEditMode ? "Actualizando..." : "Creando..."}
                        </span>
                      </div>
                    ) : (
                      <span>
                        {isEditMode
                          ? "Actualizar conductor"
                          : "Crear conductor"}
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        ) : (
          <div className="flex flex-col h-full sm:max-h-[95vh] min-h-full justify-center items-center overscroll-contain">
            <div className="text-center p-6 sm:p-8 max-w-sm mx-auto">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <CheckCircle2 className="h-8 w-8 sm:h-10 sm:w-10 text-green-600" />
              </div>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 break-words">
                {isEditMode ? "Conductor actualizado" : "Conductor creado"}
              </h3>

              <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8 break-words leading-relaxed">
                {isEditMode
                  ? "La información del conductor ha sido actualizada exitosamente."
                  : "El nuevo conductor ha sido añadido al sistema correctamente."}
              </p>

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowSuccess(false);
                    if (refreshDrivers) {
                      refreshDrivers();
                    }
                    if (onClose) onClose();
                    setIsOpen(false);
                  }}
                  className="w-full sm:w-auto px-6 py-2.5 h-11 bg-green-600 hover:bg-green-700 text-white transition-all duration-200 shadow-sm"
                >
                  <span>Continuar</span>
                </Button>

                {!isEditMode && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setShowSuccess(false);
                      resetForm();
                      setErrors({});
                    }}
                    className="w-full sm:w-auto px-6 py-2.5 h-11 border border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                  >
                    <span>Crear otro conductor</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
